import React, { useState, FC, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import BootstrapTable from 'react-bootstrap-table-next'
import ToolkitProvider from 'react-bootstrap-table2-toolkit'
import { Modal, Button, Form } from 'react-bootstrap'
import { Formik } from 'formik'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { CircularProgress } from '@material-ui/core'
import { formatUTCDate } from '../../../utils/stringTools'
import { AcknowledgmentSchema } from '../../../utils/validation'
import RootState from '../../../types/RootState'
import { mainColors } from '../../../assets/colors'
import './index.css'

interface Props {
  show: boolean;
  state: any;
  submittingAcknowledge: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

const AcknowledgmentModal: FC<Props> = (props: Props) => {
  const { show, state, submittingAcknowledge, onClose, onSubmit } = props
  const { loading, error, success } = state

  let modalText = ''
  let subModalText = ''
  let mainModalIcon
  const errorText = state.error !== false && state.error !== undefined  ? state.error.response.text : ''

  const [atBottomOfAcknowledgment, setAtBottomOfAcknowledgment] = useState(false)
  const { carePlanToSign } = useSelector((state: RootState) => ({
    carePlanToSign: state.carePlan.carePlanToSign.data,
  }))
  
  if (success) {
    mainModalIcon = <FontAwesomeIcon icon={['fas', 'check-circle']} color={mainColors.successGreen} className='modal-success-icon' />
    subModalText = 'Success!'
    modalText = 'You have acknowledged your Member Care Plans.'
  }

  if (error) {
    mainModalIcon = errorText.includes('could not be sent') ? <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} color='orange' className='modal-success-icon' /> : <FontAwesomeIcon icon={['fas', 'exclamation-circle']} color={mainColors.loadingBlue} className='modal-success-icon' />
    subModalText = errorText.includes('could not be sent') ? 'Warning' : 'Error!'
    modalText = errorText.includes('could not be sent') ? '' : 'The data could not be saved'
  }

  const carePlanTable = useRef()
  const handleScroll = (event) => {
    const bottom = event.target.clientHeight >= ( event.target.scrollHeight - event.target.scrollTop - 5 )
    if (bottom) {
      setAtBottomOfAcknowledgment(bottom)
    }
  }

  useEffect(() => {
    if (carePlanTable.current) {
      const div = document.getElementById('carePlanToSignTable')
      const hasVerticalScrollbar = div && div.children[1] && div.children[1].scrollHeight > div.children[1].clientHeight
  
      // if no scrollbar exists, no need to check if scroll is at the bottom
      if (!hasVerticalScrollbar) {
        setAtBottomOfAcknowledgment(true)
      }
    }
  }, [carePlanTable.current]) // eslint-disable-line react-hooks/exhaustive-deps

  const columns = [{
    dataField: 'memberFullName',
    text: 'Member Name',
    sort: false, 
  }, {
    dataField: 'providerName',
    text: 'Essential Provider Location Name',
    sort: false,
  }, {
    dataField: 'carePlanDate',
    text: 'Care Plan Date',
    sort: false,
    formatter: (cellContent) => ( cellContent && formatUTCDate(cellContent) ) || '',
  }]

  return (
    <Modal size="lg" show={show} onHide={()=> {}}>
      <Modal.Header closeButton id="switch-organization-header">
        <FontAwesomeIcon icon={['fas', 'user-circle']} size="lg" className="switch-organization-header-icon" />
        <Modal.Title id="switch-organization-header-title">Required Acknowledgment</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {
          !submittingAcknowledge ? (
            <div>
              <p>New Care Plans for members you serve have been uploaded. You can view them in the &quot;Care Plan&quot; tab</p>
              <ToolkitProvider
                keyField='carePlanProviderSignatureId'
                bootstrap4
                data={carePlanToSign}
                columns={columns}
              >
                {
                  props => (
                    <div id='carePlanTableContainer' className='care-plan-table' style={{width: '100%'}} onScroll={handleScroll}>
                      <BootstrapTable
                        id='carePlanToSignTable'
                        ref={carePlanTable}
                        bordered={false}
                        striped
                        condensed
                        bootstrap4
                        { ...props.baseProps }
                      />
                    </div>
                  )
                }
              </ToolkitProvider>
              <Formik
                initialValues={{
                  requiredAcknowledgment: false,
                }}
                enableReinitialize
                validationSchema={AcknowledgmentSchema}
                onSubmit={( values, { setSubmitting }) => {
                  setAtBottomOfAcknowledgment(false)
                  onSubmit()
                  setSubmitting(false)
                }}
              >
                {({
                  values,
                  errors,
                  handleSubmit,
                  setFieldValue
                }) => (
                  <div className='care-plan-to-sign-checkbox' >
                    <Form noValidate onSubmit={handleSubmit} className='care-plan-form'>
                      <Form.Check
                        type='checkbox'
                        id='requiredAckowledgment'
                        label='By checking this box, you acknowledge receipt of Care Plan(s).'
                        onChange={() => setFieldValue('requiredAcknowledgment', !values.requiredAcknowledgment)}
                        isInvalid={!!errors.requiredAcknowledgment}
                      />
                      {
                        errors.requiredAcknowledgment && (
                          <Form.Text className="form-error" style={{ textAlign: 'center' }}>
                            {errors.requiredAcknowledgment}
                          </Form.Text>
                        )
                      }
                      <div className="switch-organization-btn-grp">
                        <Button id="switch-organization-add-btn" type='submit' disabled={!atBottomOfAcknowledgment}>
                          Submit
                        </Button>
                      </div>
                    </Form>
                  </div>
                )}
              </Formik>
            </div>
          ) : (
            loading ? (<CircularProgress size={40} />) : (
              <div className='modal-loading-container'>
                {mainModalIcon}
                <span className='modal-success-text'>{subModalText}</span>
                <span className='modal-success-text' style={error!== false ? { width: '100%' } : {}}>
                  {modalText}
                  {
                    error!== false ? (<span style={{width: '100%'}} dangerouslySetInnerHTML={{__html: errorText}} />) : null
                  }
                </span>
                <div className='modal-button-grp' style={{marginTop: 15}}>
                  <Button variant='info' className='progress-card-btn' onClick={onClose}>Close</Button>
                </div>
              </div>
            )
          )
        }
      </Modal.Body>
    </Modal>
  )
}

export default AcknowledgmentModal 